/**
 * Mises en forme de l'écran Activité — **fonctions pures**, éprouvables au banc
 * (`ARCHITECTURE_V2.md` §5.3).
 *
 * Même rôle que `weightFields.ts` pour la pesée, et le même motif : ce sont les
 * seuls calculs de cet écran, autant qu'ils soient à part et sans React autour.
 *
 * 🔴 **Aucune de ces fonctions ne combine deux grandeurs.** Il n'y a ici ni
 * soustraction de kcal, ni « restant ajusté », ni conversion d'une dépense en
 * quoi que ce soit d'autre — et il ne doit jamais y en avoir : c'est le mur de
 * neutralité du §5.3, tenu à l'endroit où il serait le plus tentant de le
 * franchir. Chaque fonction met en forme **une** valeur.
 */

/**
 * `null` se dit « — », partout et de la même façon.
 *
 * ⚠️ **Et jamais « 0 ».** Une donnée que la montre n'a pas mesurée n'est pas une
 * valeur nulle : afficher « 0 pas » un jour sans données affirmerait qu'on n'a
 * pas bougé. C'est la distinction déjà tenue par la charge d'un passage à
 * l'étape 7 — « `null` et jamais zéro […] une courbe qui plonge à zéro
 * raconterait une régression qui n'a pas eu lieu ».
 */
export const ABSENT = '—';

/** Entier séparé à la française — « 2 340 ». */
export function formatCount(value: number | null): string {
  if (value === null) return ABSENT;
  return Math.round(value).toLocaleString('fr-FR');
}

/** Une décimale, séparateur français — « 42,5 ». */
export function formatDecimal(value: number | null, digits = 1): string {
  if (value === null) return ABSENT;
  return value.toLocaleString('fr-FR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Durée en secondes → « 1 h 12 » ou « 48 min ».
 *
 * ⚠️ **Les heures ne se disent pas en décimal** (« 1,2 h ») : on lit une durée
 * d'effort comme on lit une horloge, et personne ne convertit 0,2 h de tête.
 * Sous l'heure, on reste en minutes entières — la seconde n'apprend rien sur une
 * sortie de trois quarts d'heure.
 */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds < 0) return ABSENT;

  const total = Math.round(seconds / 60);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;

  if (hours === 0) return `${String(minutes)} min`;
  return `${String(hours)} h ${String(minutes).padStart(2, '0')}`;
}

/**
 * Distance en mètres → « 12,4 km » ou « 840 m ».
 *
 * ⚠️ Le seuil est à 1 km, et l'unité change **avec** lui : « 0,8 km » se lit
 * moins bien que « 840 m », et « 12 400 m » moins bien que « 12,4 km ».
 */
export function formatDistance(meters: number | null): string {
  if (meters === null || meters < 0) return ABSENT;
  if (meters < 1000) return `${String(Math.round(meters))} m`;
  return `${formatDecimal(meters / 1000)} km`;
}

/** Dénivelé → « 420 m ». Toujours en mètres : c'est l'unité dont on parle. */
export function formatElevation(meters: number | null): string {
  if (meters === null) return ABSENT;
  return `${formatCount(meters)} m`;
}

/**
 * Sommeil en secondes → « 7 h 20 ».
 *
 * Volontairement le **même** format qu'une durée d'activité : ce sont les mêmes
 * heures lues par le même œil, et deux formats sur un même écran feraient
 * hésiter. C'est le raisonnement déjà tenu pour `formatSeconds()` entre le repos
 * d'un bloc et la durée d'une série (étape 5).
 */
export function formatSleep(seconds: number | null): string {
  return formatDuration(seconds);
}

/**
 * Fréquence cardiaque → « 48 bpm ».
 *
 * ⚠️ Arrondie à l'entier : intervals.icu rend parfois une moyenne décimale, et
 * « 142,7 bpm » suggère une précision que la mesure n'a pas.
 */
export function formatHr(bpm: number | null): string {
  if (bpm === null) return ABSENT;
  return `${formatCount(bpm)} bpm`;
}

/** Énergie → « 620 kcal ». */
export function formatKcal(kcal: number | null): string {
  if (kcal === null) return ABSENT;
  return `${formatCount(kcal)} kcal`;
}

/**
 * Heure de début d'une activité, dans le fuseau de **l'appareil**.
 *
 * ⚠️ **Écart assumé, et il faut le dire** : la *date* d'une activité est rangée
 * par le serveur dans le fuseau **réglé** (§5.3), tandis que cette heure-ci se
 * lit dans celui de l'appareil. Les deux ne se contredisent pas — l'une classe,
 * l'autre situe dans la journée qu'on est en train de vivre —, et c'est la même
 * répartition que partout depuis les retours de l'étape 8 : « ce qu'un écran
 * appelle aujourd'hui doit être le jour de l'appareil qu'on tient », le serveur
 * gardant sa propre notion du jour pour ce qu'il décide seul.
 */
export function formatStartTime(startAt: number | null): string {
  if (startAt === null) return ABSENT;
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(startAt));
}

/**
 * Horodatage de la dernière ingestion → « aujourd'hui à 14:30 », « le 3 sept. à
 * 08:00 ».
 *
 * ⚠️ **Une date absolue plutôt qu'un « il y a 12 minutes ».** Un écart relatif
 * se périme à l'écran sans que rien ne le réactualise — et c'est justement
 * l'écran qu'on ouvre après avoir laissé l'application en arrière-plan. Une
 * heure ne ment jamais.
 */
export function formatSyncedAt(at: number | null, now: Date = new Date()): string {
  if (at === null) return 'jamais';

  const date = new Date(at);
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const time = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);

  if (sameDay) return `aujourd’hui à ${time}`;

  const day = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' }).format(date);
  return `le ${day} à ${time}`;
}

/**
 * Type d'activité, en français quand on le connaît.
 *
 * 🔴 **Tout ou rien, exactement la règle des noms d'exercices** (étape 4,
 * `exerciseFr.ts`) : un type inconnu garde son libellé source plutôt que de
 * produire du franglais. La table est courte et fermée côté Coros ; l'enrichir
 * suffit à la faire grandir, et un type non traduit reste parfaitement lisible.
 *
 * ⚠️ **Traduit à l'AFFICHAGE ici, et non à l'ingestion** — l'inverse du
 * référentiel d'exercices, et pour une raison précise : là-bas la recherche est
 * **locale** et le tri se fait sur le nom, donc traduire au dernier moment
 * aurait donné « une liste française qu'il faut chercher en anglais ». Ici rien
 * n'est cherché ni trié par type : la colonne garde le vocabulaire de la source,
 * ce qui la laisse comparable à intervals.icu quand on va y vérifier quelque
 * chose.
 */
const TYPE_FR: Readonly<Record<string, string>> = {
  Ride: 'Vélo',
  VirtualRide: 'Vélo virtuel',
  GravelRide: 'Gravel',
  MountainBikeRide: 'VTT',
  Run: 'Course',
  TrailRun: 'Trail',
  VirtualRun: 'Course virtuelle',
  Walk: 'Marche',
  Hike: 'Randonnée',
  Swim: 'Natation',
  WeightTraining: 'Musculation',
  Workout: 'Séance',
  Yoga: 'Yoga',
  Rowing: 'Aviron',
  Elliptical: 'Elliptique',
  Crossfit: 'Cross-training',
  AlpineSki: 'Ski alpin',
  NordicSki: 'Ski de fond',
  Snowboard: 'Snowboard',
  Kayaking: 'Kayak',
  Climbing: 'Escalade',
  Soccer: 'Football',
  Tennis: 'Tennis',
  Golf: 'Golf',
  Other: 'Autre',
};

export function formatType(type: string | null): string {
  if (type === null) return ABSENT;
  return TYPE_FR[type] ?? type;
}
